import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { useSession } from '../../lib/store';
import { Card, Notice, PageHeading, Row, Spinner } from '../../components/Shell';
import { UpdateCard } from '../../components/VisitUpdate';
import { QuoteTable } from '../../components/Money';
import { BookingStamp, PaymentStamp } from '../../components/Stamp';
import { SelectField, TextField } from '../../components/Field';
import { dateRange, dateTime, longDate, money, titleCase } from '../../lib/format';
import type { Blocker, Booking, Payment, PaymentSummary, Pet, ServiceSummary, VisitReport } from '../../lib/types';

const METHOD_LABELS: Record<string, string> = {
  card: 'Card',
  zelle: 'Zelle',
  venmo: 'Venmo',
  cash: 'Cash',
  bank_transfer: 'Bank transfer',
  check: 'Check',
};

/**
 * One booking, from the client's side: what was agreed, what is still owed,
 * what paperwork is outstanding, and how their pet is doing while in care.
 */
export function BookingDetail() {
  const { bookingId } = useParams<{ bookingId: string }>();
  const [searchParams] = useSearchParams();
  const session = useSession();
  const tenantId = session.tenant?.tenantId;
  const timezone = session.tenant?.timezone;

  const [booking, setBooking] = useState<Booking | null>(null);
  const [pets, setPets] = useState<Pet[]>([]);
  const [service, setService] = useState<ServiceSummary | null>(null);
  const [blockers, setBlockers] = useState<Blocker[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [summary, setSummary] = useState<PaymentSummary | null>(null);
  const [reports, setReports] = useState<VisitReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  const paymentResult = searchParams.get('payment');

  const load = useCallback(async () => {
    if (!tenantId || !bookingId) return;
    try {
      const [detail, paymentData, reportData] = await Promise.all([
        api<{ booking: Booking; pets: Pet[]; service: ServiceSummary; blockers: Blocker[] }>(
          `/tenants/${tenantId}/bookings/${bookingId}`
        ),
        api<{ payments: Payment[]; summary: PaymentSummary }>(
          `/tenants/${tenantId}/bookings/${bookingId}/payments`
        ),
        api<{ reports: VisitReport[] }>(`/tenants/${tenantId}/bookings/${bookingId}/reports`).catch(
          () => ({ reports: [] as VisitReport[] })
        ),
      ]);

      setBooking(detail.booking);
      setPets(detail.pets);
      setService(detail.service);
      setBlockers(detail.blockers);
      setPayments(paymentData.payments);
      setSummary(paymentData.summary);
      setReports(reportData.reports);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'This booking could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [tenantId, bookingId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-16">
        <Spinner label="Opening the booking" />
      </div>
    );
  }

  if (!booking || !tenantId || !bookingId) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-16">
        <Notice tone="error">{error ?? 'This booking is not available.'}</Notice>
      </div>
    );
  }

  const payByCard = () => {
    setWorking(true);
    void api<{ checkoutUrl: string }>(`/tenants/${tenantId}/bookings/${bookingId}/checkout`, {
      method: 'POST',
      body: {},
    })
      .then((result) => {
        window.location.href = result.checkoutUrl;
      })
      .catch((err) => {
        setError(err instanceof ApiError ? err.message : 'Card payment could not be started.');
        setWorking(false);
      });
  };

  const cancel = () => {
    setWorking(true);
    void api(`/tenants/${tenantId}/bookings/${bookingId}/status`, {
      method: 'POST',
      body: { action: 'cancel' },
    })
      .then(load)
      .catch((err) => setError(err instanceof ApiError ? err.message : 'The booking could not be cancelled.'))
      .finally(() => setWorking(false));
  };

  const cancellable = ['requested', 'awaiting_documents', 'awaiting_payment', 'confirmed'].includes(
    booking.status
  );
  const owes = (summary?.outstanding ?? 0) > 0;
  const cardAccepted = (session.tenant?.acceptedPaymentMethods ?? []).includes('card');
  const manualMethods = (session.tenant?.acceptedPaymentMethods ?? []).filter((m) => m !== 'card');

  return (
    <div className="mx-auto max-w-3xl px-5 pb-24 pt-12">
      <PageHeading
        eyebrow={service?.name ?? titleCase(booking.serviceCode)}
        title={dateRange(booking.startDate, booking.endDate, timezone)}
        actions={<BookingStamp status={booking.status} />}
      />

      {paymentResult === 'success' && (
        <div className="mb-8">
          <Notice tone="ok" title="Payment received">
            Thank you. It may take a moment to show below.
          </Notice>
        </div>
      )}
      {paymentResult === 'cancelled' && (
        <div className="mb-8">
          <Notice tone="warn">Payment was not completed. Your dates are still held.</Notice>
        </div>
      )}
      {error && (
        <div className="mb-8">
          <Notice tone="error">{error}</Notice>
        </div>
      )}

      {blockers.length > 0 && (
        <div className="mb-8">
          <Notice tone="warn" title="This cannot be confirmed yet">
            <ul className="mt-1 space-y-1">
              {blockers.map((blocker) => (
                <li key={`${blocker.code}-${blocker.petId ?? ''}`}>
                  {blocker.message}
                  {blocker.clientActionable && blocker.petId && (
                    <Link className="ml-2 underline" to={`/portal/pets/${blocker.petId}`}>
                      Fix this
                    </Link>
                  )}
                  {blocker.clientActionable && !blocker.petId && (
                    <Link className="ml-2 underline" to="/portal">
                      Fix this
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </Notice>
        </div>
      )}

      <div className="grid gap-6 sm:grid-cols-2">
        <Card tab="The stay" className="p-5">
          <dl>
            <Row label="Service">{service?.name ?? titleCase(booking.serviceCode)}</Row>
            <Row label="Starts">{dateTime(booking.startAt, timezone)}</Row>
            <Row label="Ends">{dateTime(booking.endAt, timezone)}</Row>
            {booking.nights > 0 && <Row label="Nights">{booking.nights}</Row>}
            {booking.visitCount && <Row label="Visits">{booking.visitCount}</Row>}
            <Row label="Pets">{pets.map((p) => p.name).join(', ') || '-'}</Row>
            <Row label="Requested">{longDate(booking.createdAt, timezone)}</Row>
          </dl>
          {booking.clientNotes && (
            <div className="mt-4 border-t border-[color:var(--color-rule)] pt-3">
              <p className="field-label">Your notes</p>
              <p className="text-sm">{booking.clientNotes}</p>
            </div>
          )}
        </Card>

        <Card tab="Price" className="p-5">
          <QuoteTable quote={booking.quote} compact />
        </Card>
      </div>

      {/* --- Payment ------------------------------------------------------- */}
      <Card tab="Payment" tint="yellow" className="mt-8 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-display text-lg font-bold">
              {owes ? `${money(summary?.outstanding ?? 0)} outstanding` : 'Nothing outstanding'}
            </p>
            {summary?.balanceDueDate && owes && (
              <p className="typed text-sm text-[color:var(--color-ink-soft)]">
                Balance due by {summary.balanceDueDate}
              </p>
            )}
          </div>
          <PaymentStamp status={booking.payment.status} flat />
        </div>

        {owes && cancellable && (
          <div className="mt-5 flex flex-wrap gap-3">
            {cardAccepted && (
              <button type="button" className="btn btn-primary" onClick={payByCard} disabled={working}>
                {working ? 'Opening' : `Pay ${money(summary?.outstanding ?? 0)} by card`}
              </button>
            )}
            {manualMethods.length > 0 && (
              <ManualPaymentForm
                tenantId={tenantId}
                bookingId={bookingId}
                methods={manualMethods}
                outstanding={summary?.outstanding ?? 0}
                onDone={load}
              />
            )}
          </div>
        )}

        {payments.length > 0 && (
          <ul className="mt-6 space-y-2 border-t border-[color:var(--color-rule)] pt-4">
            {payments.map((payment) => (
              <li key={payment.paymentId} className="flex flex-wrap items-center justify-between gap-3 text-sm">
                <span>
                  <span className="typed">{money(payment.amount + payment.surcharge)}</span>
                  <span className="ml-2 text-[color:var(--color-ink-soft)]">
                    {METHOD_LABELS[payment.method] ?? payment.method}, {titleCase(payment.kind)}
                  </span>
                </span>
                <span className="font-mono text-xs text-[color:var(--color-ink-faint)]">
                  {payment.status === 'awaiting_confirmation'
                    ? 'Waiting for your sitter to confirm'
                    : titleCase(payment.status)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* --- Visit updates -------------------------------------------------- */}
      {reports.length > 0 && (
        <section className="mt-10">
          <h2 className="mb-4 text-2xl">How they have been</h2>
          <ul className="space-y-5">
            {reports.map((report) => (
              <li key={report.reportId}>
                <UpdateCard report={report} timezone={timezone} />
              </li>
            ))}
          </ul>
        </section>
      )}

      {cancellable && (
        <div className="mt-12 border-t border-[color:var(--color-rule)] pt-6">
          <p className="text-sm text-[color:var(--color-ink-soft)]">
            Cancelling with {session.tenant?.cancellationNoticeDays ?? 7} days notice or more is free.
            Less than that forfeits the deposit plus one night.
          </p>
          <button type="button" className="btn btn-danger mt-4" onClick={cancel} disabled={working}>
            Cancel this booking
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Declaring an off-platform payment. This records an intent, not a payment:
 * the sitter confirms when the money actually lands, which is the only honest
 * way to track Zelle or cash.
 */
function ManualPaymentForm({
  tenantId,
  bookingId,
  methods,
  outstanding,
  onDone,
}: {
  tenantId: string;
  bookingId: string;
  methods: string[];
  outstanding: number;
  onDone: () => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [method, setMethod] = useState(methods[0]);
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [instructions, setInstructions] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  if (!open) {
    return (
      <button type="button" className="btn btn-secondary" onClick={() => setOpen(true)}>
        I paid another way
      </button>
    );
  }

  if (sent) {
    return (
      <div className="w-full">
        <Notice tone="ok" title="Noted">
          Your sitter will confirm once the money arrives.
          {instructions && <p className="mt-2">{instructions}</p>}
        </Notice>
      </div>
    );
  }

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);

    void api<{ instructions?: string }>(`/tenants/${tenantId}/bookings/${bookingId}/payments`, {
      method: 'POST',
      body: { method, clientReference: reference || undefined },
    })
      .then(async (result) => {
        setInstructions(result.instructions ?? null);
        setSent(true);
        await onDone();
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : 'That could not be recorded.'))
      .finally(() => setBusy(false));
  };

  return (
    <form onSubmit={submit} className="w-full space-y-4 border-t border-[color:var(--color-rule)] pt-4">
      <SelectField
        label="How you paid"
        value={method}
        onChange={setMethod}
        options={methods.map((m) => ({ value: m, label: METHOD_LABELS[m] ?? m }))}
      />
      <TextField
        label="Reference"
        value={reference}
        onChange={setReference}
        placeholder="The memo or confirmation number, if there is one"
      />
      {error && <Notice tone="error">{error}</Notice>}
      <div className="flex gap-2">
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? 'Recording' : `Tell my sitter I sent ${money(outstanding)}`}
        </button>
        <button type="button" className="btn btn-quiet" onClick={() => setOpen(false)}>
          Cancel
        </button>
      </div>
    </form>
  );
}
